import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "../Assets/svgs/CloseIcon";
import { Loader } from "../Common/Loader";
import MultiSelectDropdown from "../Common/MultiSelectDropdown";
import { nameWithNumberValidation } from "../Common/Validations";
import { getAssistantDocumentsList } from "../Redux/Slice/Documents/AssistantDocumentListSlice";
import { addKnowledgeBase } from "../Redux/Slice/KnowledgeBase/AddKnowledgeBaseSlice";
import { ErrorModel } from "./ErrorModel";

const AddKnowledgeBaseModal = ({ closePopup, onSuccess }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    knowledgeBaseName: "",
    documents: [],
  });
  const [formError, setFormError] = useState({});
  const [errorModal, setErrorModal] = useState({ visible: false, message: "" });
  const knowledgeBaseListData = useSelector(
    (state) => state.documentList.documentsListData
  );

  const formattedKnowledgeBaseDocumentList = useMemo(() => {
    let _formattedDataList = Array.isArray(knowledgeBaseListData?.data)
      ? knowledgeBaseListData?.data?.map((assistant) => {
          return { value: assistant?.vapiFileId, label: assistant?.name };
        })
      : [];
    return _formattedDataList;
  }, [knowledgeBaseListData, knowledgeBaseListData?.data]);

  const handleChange = async (e) => {
    let { value, name } = e?.target;
    value = value.trimStart();
    const isValid = nameWithNumberValidation(value);
    if (isValid || value.trim() === "") {
      setFormError({ ...formError, knowledgeBaseNameError: "" });
    } else {
      setFormError({
        ...formError,
        knowledgeBaseNameError: "Please enter valid group name",
      });
    }
    let capitalized = value.charAt(0).toString().toUpperCase();
    value = capitalized + value.substring(1);

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const getDocumentsList = async () => {
    setIsLoading(true);
    await dispatch(
      getAssistantDocumentsList({
        searchText: "",
        page: 1,
        limit: 200,
        type: "dropdown",
      })
    );
    setIsLoading(false);
  };

  const handleSubmit = async () => {
    let body = {
      name: formData.knowledgeBaseName,
      fileIds: formData.documents,
    };
    setIsLoading(true);
    try {
      const result = await dispatch(addKnowledgeBase(body));
      if (
        addKnowledgeBase.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        onSuccess("Knowledge Base added Successfully!");
      } else {
        setIsLoading(false);
        showErrorMessage(result?.payload?.error?.message);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const hasFormError = useMemo(() => {
    return Object.values(formError).some((error) => error);
  }, [formError]);

  const isDisabled = useMemo(() => {
    return !formData.knowledgeBaseName || !formData.documents.length;
  }, [formData]);

  useEffect(() => {
    getDocumentsList();
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  if (errorModal.visible) {
    return <ErrorModel message={errorModal.message} />;
  }

  return (
    <div className="modal modal--planSummery modal--open">
      <div className="modal__dialog">
        <div className="modal__content modal__content--md">
          <div className="modal__header">
            <h1 className="modal__header--title">Create a knowledge base</h1>
            <button
              className="btn btn__transparent btnClose"
              onClick={closePopup}
            >
              <CloseIcon />
            </button>
          </div>
          <div className="modal__body  modal-body-left">
            <div className="createFormGrid">
              <div className="createFormGrid__row">
                <div className="formCol">
                  <div className="form__Field form__Field--full">
                    <label
                      for="knowledgeBaseName"
                      className="form__label form__label--flex"
                    >
                      <span>
                        Knowledge Base Name
                        <sup className="form__label__asterisk">*</sup>
                      </span>
                    </label>
                    <input
                      type="text"
                      id="knowledgeBaseName"
                      name="knowledgeBaseName"
                      value={formData?.knowledgeBaseName}
                      onChange={handleChange}
                      className="form__input form__input--sm"
                      placeholder="Knowledge Base Name"
                      autoComplete="off"
                    />
                    {formError?.knowledgeBaseNameError ? (
                      <div className="form__error">
                        {formError?.knowledgeBaseNameError}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="createFormGrid__row">
                <div className="formCol">
                  <div className="form__Field form__Field--full">
                    <label
                      for="documents"
                      className="form__label form__label--flex"
                    >
                      <span>
                        Documents
                        <sup className="form__label__asterisk">*</sup>
                      </span>
                    </label>
                    <MultiSelectDropdown
                      id="documents"
                      name="documents"
                      options={formattedKnowledgeBaseDocumentList}
                      selectedValue={formData.documents}
                      onChange={(docList) => {
                        setFormData({
                          ...formData,
                          documents: Array.from(
                            docList,
                            (option) => option?.value
                          ),
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal__footer">
            <div className="btn__group">
              <button className="btn btn__white" onClick={closePopup}>
                Cancel
              </button>
              <button
                className="btn btn__primary"
                disabled={isDisabled || hasFormError}
                onClick={handleSubmit}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal__backdrop"></div>
    </div>
  );
};

export default AddKnowledgeBaseModal;
